// src/store/store.js

import { createStore } from 'vuex';
import { initialUserState } from '@/models/user.js';
import { eventService } from '../services/EventService';
import { accordionModule } from './modules/accordion';

const store = createStore({
   modules: {
    accordion: accordionModule
  },
  state: {
    user: { ...initialUserState },
    selectedCompany: null,
    selectedUser: null,
  },
  mutations: {

    setUser(state, user) {
      if (user) {
        // Reset to clean state and update with all user data
        state.user = { ...initialUserState, isLoggedIn: true };
        
        Object.keys(initialUserState).forEach(key => {
          if (user[key] !== undefined) {
            state.user[key] = user[key];
          }
        });
        
        localStorage.setItem('user', JSON.stringify(state.user));
      } else {
        // Handle logout
        state.user = { ...initialUserState, isLoggedIn: false };
        state.selectedUser = null;
        state.selectedCompany = null;
        
        localStorage.removeItem('user');
        localStorage.removeItem('selectedUser');
        localStorage.removeItem('selectedCompany');
      }
    },

    // Partial user update - used for Firebase auth updates and other partial changes
    updateUser(state, updates) {
      console.log('mutation UPDATE USER: ', updates);
      if (updates && state.user) {
        // Only update specified fields while preserving existing data
        state.user = {
          ...state.user,
          ...updates
        };
        
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
      
    setSelectedUser(state, user) {
      if (user) {
        // Create a clean copy of the user object
        state.selectedUser = { ...user };
        localStorage.setItem('selectedUser', JSON.stringify(state.selectedUser));
      } else {
        state.selectedUser = null;
        localStorage.removeItem('selectedUser');
      }
    },

    setSelectedCompany(state, company) {
      if (company) {
        // Create a clean copy of the company object
        state.selectedCompany = { ...company };
        localStorage.setItem('selectedCompany', JSON.stringify(state.selectedCompany));
      } else {
        state.selectedCompany = null;
        localStorage.removeItem('selectedCompany');
      }
    },

    updateSelectedUser(state, payload) {
      if (state.selectedUser && payload) {
        // Create a new object instead of mutating directly
        state.selectedUser = { ...state.selectedUser, ...payload };
        localStorage.setItem('selectedUser', JSON.stringify(state.selectedUser));
        
        // If selected user is the current user, update user state too
        if (state.selectedUser.uid === state.user.uid) {
          // Only update properties that exist in initialUserState
          const validUpdates = {};
          for (const key of Object.keys(initialUserState)) {
            if (payload[key] !== undefined) {
              validUpdates[key] = payload[key];
            }
          }
          state.user = { ...state.user, ...validUpdates };
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      }
    },

    updateSelectedCompany(state, updatedData) {
      if (state.selectedCompany && updatedData) {
        // Create a new object instead of mutating directly
        state.selectedCompany = { ...state.selectedCompany, ...updatedData };
        localStorage.setItem('selectedCompany', JSON.stringify(state.selectedCompany));
      }
    },
  },

  actions: {
    setUser({ commit }, user) {
      commit('setUser', user);
    },

    updateUser({ commit }, updates) {
      commit('updateUser', updates);
    },

    setSelectedUser({ commit }, user) {
      commit('setSelectedUser', user);
    },

    setSelectedCompany({ commit }, company) {
      commit('setSelectedCompany', company);
    },

    updateSelectedUser({ commit }, payload) {
      commit('updateSelectedUser', payload);
    },

    updateSelectedCompany({ commit }, updatedData) {
      commit('updateSelectedCompany', updatedData);
    },

    logout({ commit }) {
      commit('setUser', null);
    }
  },

  getters: {
    isUserLoggedIn: (state) => state.user.isLoggedIn,
    userRole: (state) => state.user.role,
    getSelectedUser: (state) => state.selectedUser,
    getSelectedCompany: (state) => state.selectedCompany,
    // Add a getter to check if user data is complete
    isUserDataComplete: (state) => {
      return Object.keys(initialUserState).every(key => 
        state.user[key] !== undefined && state.user[key] !== null
      );
    }
  },

 plugins: [
    (store) => {
      store.watch(
        (state) => state.selectedCompany,
        (newCompany) => {
          if (newCompany && newCompany.companyNumber) {
            eventService.setSelectedCompany(newCompany.companyNumber);
          }
        },
        { immediate: true }
      );
    }
  ]
    
});

export default store;

